import { Injectable } from '@angular/core';
import {
	Subject,
	Subscription,
	fromEvent,
	interval,
	startWith,
	takeWhile,
} from 'rxjs';
import { StorageService } from './storage.service';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { ModalType } from '../app.constants';
import { ModalController } from '@ionic/angular';
import {
	LogoutWarningComponent,
	LogoutWarningDismissResult,
} from '../components/logout-warning/logout-warning.component';
import { environment } from '../../environments/environment';
import dayjs, { Dayjs } from 'dayjs';
import {
	KioskRestartWarningComponent,
	RestartWarningDismissResultKiosk,
} from '../components/kiosk-restart-warning/kiosk-restart-warning.component';

@Injectable({
	providedIn: 'root',
})
export class IdleTimerService {
	countdownTimer = Subscription.EMPTY;
	countdownTimerKiosk = Subscription.EMPTY;
	second = 1000;
	remainingSeconds: number =
		60 * environment.settings.kioskSessionLengthInMinutes;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	private timerInterval: any;
	private eventCallback = new Subject<number>();
	eventCallback$ = this.eventCallback.asObservable();
	logoutWarningTimestamp: Dayjs;
	kioskRestartWarningTimestamp: Dayjs;
	visibilitySubscription: Subscription;
	isWindowActive: boolean;

	isBaseTimerDisabled: boolean;

	// Routes where idle timer should be completely disabled.
	// This is used to prevent the idle timer from inadvertently logging out the user and clearing the config.
	private routesWithoutIdleTimer = [
		'/print-preview', // Virtual printer page
		// If more routes need to be added move the routes to the environment files
	];

	constructor(
		public authService: AuthService,
		public router: Router,
		public modalCtrl: ModalController,
	) {
		this.initVisibility();
	}

	/**
	 * If the user has switched to a different tab, go ahead and
	 * log them out. Here, we tap into the documents "visibility"
	 * to see if the user has the page "active".
	 */
	initVisibility() {
		const visibilitychange$ = fromEvent(document, 'visibilitychange');

		this.visibilitySubscription = visibilitychange$
			.pipe(startWith(true))
			.subscribe(() => {
				this.isWindowActive = document.visibilityState === 'visible';
			});
	}

	setlogoutWarningTimestamp(sessionLengthInMinutes: number) {
		this.logoutWarningTimestamp = dayjs().add(sessionLengthInMinutes, 'minute');
	}

	setWarningTimestampKiosk(sessionLengthInMinutes: number) {
		this.kioskRestartWarningTimestamp = dayjs().add(
			sessionLengthInMinutes,
			'minute',
		);
	}

	isLogoutWarningTimeoutEnded(): boolean {
		const now = dayjs();
		return this.logoutWarningTimestamp.isAfter(now);
	}

	isRestartWarningTimeoutEndedKiosk(): boolean {
		const now = dayjs();
		return this.kioskRestartWarningTimestamp.isAfter(now);
	}

	toggleIdleTimer(isBaseTimerDisabled: boolean) {
		this.isBaseTimerDisabled = isBaseTimerDisabled;
		if(this.isBaseTimerDisabled) {
			this.countdownTimer.unsubscribe();
			return;
		}
		this.initIdleTimer();
	}

	/**
	 * Timer resets when:
	 *   - the user has navigated to a different page
	 *   - the user has completed an action that requires an API call
	 *
	 * When the timer runs out:
	 *   - an alert modal is displayed
	 *     - the user can continue (extend) their session
	 *     - the user can logout
	 *     - the user is logged out automatically after 1 minute has elapsed
	 *
	 * Time is defined by `sessionLengthInMinutes` set by app configs
	 * with a fallback set in the environment.
	 */
	initIdleTimer() {
		// Check if current route should skip idle timer entirely
		if (this.shouldSkipIdleTimer()) {
			this.countdownTimer.unsubscribe();
			return;
		}

		// always unsubscribe and spin up a new timer.
		this.countdownTimer.unsubscribe();

		// Ignore pages when the user isn't logged in.
		if (!StorageService.authToken || this.isBaseTimerDisabled) {
			return;
		}

		const sessionLengthInMinutes =
			StorageService.appConfigs?.sessionLengthInMinutes ||
			environment.settings.sessionLengthMinutes;

		this.setlogoutWarningTimestamp(sessionLengthInMinutes);

		const time = interval(this.second);

		this.countdownTimer = time
			.pipe(takeWhile(() => this.isLogoutWarningTimeoutEnded()))
			.subscribe({
				complete: async () => {
					if (!this.isWindowActive) {
						console.log('Inactive window');
						await this.logout();
						return;
					}

					const data: LogoutWarningDismissResult =
						await this.showLogoutWarning();

					if (data.action === 'extendSession') {
						this.initIdleTimer();
						return;
					}

					if (data.action === 'logout') {
						this.logout();
						return;
					}
				},
			});
	}

	/**
	 * [Kiosk]
	 * Timer resets when:
	 *   - the user has navigated to a different page
	 *   - the user has completed an action that requires an API call
	 *
	 * When the timer runs out:
	 *   - an alert modal is displayed
	 *     - the user can continue (extend) their session
	 *     - the user can logout
	 *     - the user is logged out automatically after 1 minute has elapsed
	 *
	 * Time is defined by `sessionLengthInMinutes` set by app configs
	 * with a fallback set in the environment.
	 */
	initIdleTimerKiosk() {
		// always unsubscribe and spin up a new timer.
		this.countdownTimerKiosk.unsubscribe();

		// Ignore pages when the user isn't logged in.
		if (!StorageService.authToken && !this.isBaseTimerDisabled) {
			return;
		}

		const kioskSessionLengthInMinutes =
			environment.settings.kioskSessionLengthInMinutes;
		const time = interval(this.second);

		this.setWarningTimestampKiosk(kioskSessionLengthInMinutes);

		this.countdownTimerKiosk = time
			.pipe(takeWhile(() => this.isRestartWarningTimeoutEndedKiosk()))
			.subscribe({
				complete: async () => {
					if (!this.isWindowActive) {
						console.log('Inactive window');
						window.location.reload();
						return;
					}

					const data: RestartWarningDismissResultKiosk =
						await this.showRestartWarningKiosk();

					if (data.action === 'extendSession') {
						this.initIdleTimerKiosk();
						return;
					}

					if (data.action === 'restart') {
						window.location.reload();
						return;
					}
				},
			});
	}

	kioskConfirmTimer() {
		this.countdownTimerKiosk.unsubscribe();
		this.timerInterval = setInterval(() => {
			if (this.remainingSeconds > 1) {
				this.remainingSeconds--;
				this.eventCallback.next(this.remainingSeconds);
			} else {
				clearInterval(this.timerInterval);
				window.location.reload();
			}
		}, this.second); // Update every 1 second (1000 milliseconds)
	}

	async logout() {
		await this.authService.logout();
		window.location.href = environment.settings.clerkSignInURL;
		return;
	}

	async showLogoutWarning(): Promise<LogoutWarningDismissResult> {
		const modal = await this.modalCtrl.create({
			component: LogoutWarningComponent,
			cssClass: ModalType.LogoutWarning,
			animated: false,
			backdropDismiss: false,
		});

		await modal.present();

		const { data } = await modal.onDidDismiss();

		return data;
	}

	async showRestartWarningKiosk(): Promise<RestartWarningDismissResultKiosk> {
		const modal = await this.modalCtrl.create({
			component: KioskRestartWarningComponent,
			cssClass: ModalType.KioskRestartWarning,
			animated: false,
			backdropDismiss: false,
		});

		await modal.present();

		const { data } = await modal.onDidDismiss();

		return data;
	}

	/**
	 * Determines if the idle timer should be completely disabled for the current route.
	 *
	 * This prevents a bug where opening certain pages (like print-preview) in a new tab
	 * causes the preview tab to trigger an idle timer logout that clears the
	 * application config and causes a display bug in the active MilkTracker tab.
	 *
	 * Routes in the routesWithoutIdleTimer array represent routes where
	 * the idle timer should not run at all.
	 *
	 * Note: This only applies in non-production environments. In production,
	 * the idle timer always runs for security reasons.
	 *
	 * @returns true if the idle timer should be skipped for the current route
	 */
	private shouldSkipIdleTimer(): boolean {
		if (StorageService.isKioskMode) { 
			return true;
		}
		
		// In production, always run the idle timer
		if (environment.production) {
			return false;
		}

		const currentUrl = window.location.href;
		const currentPath = window.location.pathname;

		const shouldSkip = this.routesWithoutIdleTimer.some(route =>
			currentUrl.includes(route) || currentPath.includes(route)
		);

		if (shouldSkip) {
			console.log(`Skipping idle timer for route: ${currentPath}`);
		}

		return shouldSkip;
	}
}
